import React, { useCallback } from 'react';
import { RangeSlider } from 'components/inputs/RangeSlider';
import { TimeSpanEditorState, TimeSpanChangeAction } from './TimeSpanEditor';
import classNames from 'classnames';
import { useTabbingContext } from 'contexts/TabbingContext';

interface ITimeSpanEditorRowProps {
  autoFocus?: boolean;
  label: string;
  id: keyof TimeSpanEditorState;
  className?: string;
  min: number;
  max: number;
  value: number;
  onChange: (change: TimeSpanChangeAction) => void;
  pathString: string;
}

const getFieldClassName = () =>
  classNames(
    'dpc-time-span-editor__text-field',
    'dpc-text-input',
    'dpc-text-input-wrapper',
    'dpc-text-input-wrapper--small',
    'dpc-text-input--hide-spinner'
  );

/**
 * Row component for a TimeSpanEditor
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TimeSpanEditorRow(props: ITimeSpanEditorRowProps) {
  const { label, onChange, id } = props;
  const tabIndex = useTabbingContext();
  // path in the ID makes it unique for when more than one are on the screen
  const fieldId = `${props.pathString}-${props.id}-field`;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field: id, value: e.target.value });
    },
    [id, onChange]
  );

  return (
    <tr>
      <td className="dpc-time-span-editor__label">
        <label htmlFor={fieldId}>{label}</label>:{' '}
      </td>
      <td>
        <RangeSlider {...props} className="dpc-time-span-editor__range" onChange={handleChange} />
      </td>
      <td className="dpc-time-span-editor__field-wrapper">
        <input
          autoFocus={props.autoFocus}
          id={fieldId}
          value={props.value + ''} // re-cast to string to avoid leading 0s
          className={getFieldClassName()}
          onChange={handleChange}
          type="number"
          max={props.max}
          min={props.min}
          tabIndex={tabIndex}
        />
      </td>
    </tr>
  );
}
